import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  Braces,
  Cable,
  ChevronDown,
  Info,
  Plus,
  Tags,
  Type,
  X,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { useNodeStore } from '@/stores/node-store';
import type { CustomNode, InputValue, NodeStruct } from '@/types/node-types';
import {
  JEV_DEFAULT_LABEL_INSTRUCTIONS,
  JEV_DEFAULT_MAX_CONCURRENCY,
  JEV_MAX_CONCURRENCY,
  JEV_MAX_LABELS,
  describeJevPlan,
  formatLabels,
  formatPercent,
  formatQuestions,
  getJevCatalogInfo,
  getJevConfig,
  previewValue,
  readJevResult,
  resolveBoundParamSample,
  validateJevConfig,
  type JevConfigError,
  type JevLabel,
  type JevQuestion,
} from '@/pages/workflow/helper/jev-classifier';
import { JsonEditorField } from './json-editor-field';
import { JevResultCard } from './jev-result-view';
import { cn } from '@/lib/utils';

interface JevClassifierPanelProps {
  nodeId: string;
  struct: NodeStruct;
  onChange: (fieldId: string, value: InputValue) => void;
  disabled?: boolean;
}

const sectionLabel = 'text-sm';
const hint = 'text-xs text-muted-foreground';
const EMPTY_LABEL: JevLabel = { name: '', criteria: '' };

/**
 * A local edit buffer for a value that lives in the store. Typing changes
 * the draft; the caller commits it on blur/Enter; when the store value
 * changes underneath (reload, JSON edit, another chip), the draft follows.
 * The resync happens during render, not in an effect, so there is no frame
 * where the input shows a stale value.
 */
function useDraft<T>(source: T): [T, (next: T) => void] {
  const [draft, setDraft] = useState(source);
  const [seen, setSeen] = useState(source);
  if (seen !== source) {
    setSeen(source);
    setDraft(source);
  }
  return [draft, setDraft];
}

function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className='h-4 w-4 text-muted-foreground cursor-help' />
      </TooltipTrigger>
      <TooltipContent>
        <p className='max-w-xs'>{text}</p>
      </TooltipContent>
    </Tooltip>
  );
}

// --- label chips -------------------------------------------------------------

interface LabelChipProps {
  label: JevLabel;
  index: number;
  error: string | null;
  isUncertain: boolean;
  disabled: boolean;
  onChange: (label: JevLabel) => void;
  onRemove: () => void;
}

/** One label as a chip: the name in bold, the criteria beside it; click to edit. */
function LabelChip({
  label,
  index,
  error,
  isUncertain,
  disabled,
  onChange,
  onRemove,
}: LabelChipProps) {
  const [editing, setEditing] = useState(label.name === '');
  const [name, setName] = useDraft(label.name);
  const [criteria, setCriteria] = useDraft(label.criteria);

  const commit = () => {
    const nextName = name.trim();
    if (nextName === '' && criteria.trim() === '') {
      onRemove();
      return;
    }
    if (nextName !== label.name || criteria.trim() !== label.criteria) {
      onChange({ name: nextName, criteria: criteria.trim() });
    }
    setEditing(false);
  };

  if (editing && !disabled) {
    return (
      <div
        className='w-full rounded-lg border border-blue-300 bg-blue-50/40 p-2 flex flex-col gap-1.5 dark:border-blue-800 dark:bg-blue-950/20'
        data-testid={`jev-label-edit-${index}`}
      >
        <div className='flex items-center gap-1.5'>
          <Input
            value={name}
            autoFocus
            placeholder='label_name'
            aria-label='Label name'
            data-testid='jev-label-name'
            className='h-8 text-xs font-mono flex-1'
            onChange={(event) =>
              setName(event.target.value.toLowerCase().replace(/\s+/g, '_'))
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commit();
              }
              if (event.key === 'Escape') {
                setName(label.name);
                setCriteria(label.criteria);
                setEditing(false);
              }
            }}
          />
          <Button
            type='button'
            size='sm'
            className='h-8 px-3 text-xs'
            data-testid='jev-label-done'
            onClick={commit}
          >
            Done
          </Button>
        </div>
        <Textarea
          value={criteria}
          placeholder='What makes an item this label? e.g. The message is about an invoice, a charge or a refund'
          aria-label='Label criteria'
          data-testid='jev-label-criteria'
          rows={2}
          className='text-xs min-h-[48px]'
          onChange={(event) => setCriteria(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              commit();
            }
          }}
        />
        {error && <p className='text-xs text-red-500'>{error}</p>}
      </div>
    );
  }

  // Two sibling controls (edit, remove) rather than a button inside a
  // button: nested interactive content is not allowed and confuses readers.
  return (
    <span
      className={cn(
        'group inline-flex max-w-full items-center gap-1 rounded-full border pl-0.5 pr-1 py-0.5 text-xs',
        error
          ? 'border-red-400 bg-red-50 dark:bg-red-950/30'
          : isUncertain
            ? 'border-dashed border-amber-400 bg-amber-50 dark:bg-amber-950/20'
            : 'border-border bg-muted-background',
        !disabled && 'hover:border-blue-400',
      )}
      data-testid={`jev-label-chip-${label.name || index}`}
      title={error ?? label.criteria}
    >
      <button
        type='button'
        disabled={disabled}
        aria-label={`Edit label ${label.name || '(unnamed)'}`}
        data-testid={`jev-label-open-${label.name || index}`}
        className='inline-flex min-w-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-left disabled:cursor-default'
        onClick={() => setEditing(true)}
      >
        <span className='font-semibold font-mono shrink-0 truncate max-w-[180px]'>
          {label.name || '(unnamed)'}
        </span>
        {label.criteria ? (
          <span className='text-muted-foreground min-w-0 truncate max-w-[220px]'>
            {label.criteria}
          </span>
        ) : null}
        {isUncertain ? (
          <span className='text-[10px] text-amber-700 dark:text-amber-300 shrink-0'>
            fallback only
          </span>
        ) : null}
      </button>
      {!disabled ? (
        <button
          type='button'
          aria-label={`Remove label ${label.name}`}
          data-testid={`jev-label-remove-${label.name || index}`}
          className='rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-foreground'
          onClick={onRemove}
        >
          <X className='size-3' />
        </button>
      ) : null}
    </span>
  );
}

// --- yes/no rows --------------------------------------------------------------

interface QuestionRowProps {
  question: JevQuestion;
  error: string | null;
  disabled: boolean;
  onChange: (question: JevQuestion) => void;
  onRemove: () => void;
}

function QuestionRow({
  question,
  error,
  disabled,
  onChange,
  onRemove,
}: QuestionRowProps) {
  const [name, setName] = useDraft(question.name);
  const [text, setText] = useDraft(
    question.question === question.name ? '' : question.question,
  );

  const commit = () => {
    const nextName = name.trim();
    const nextText = text.trim();
    if (nextName === '' && nextText === '') {
      onRemove();
      return;
    }
    const next = {
      name: nextName || nextText,
      question: nextText || nextName,
    };
    if (next.name !== question.name || next.question !== question.question) {
      onChange(next);
    }
  };

  return (
    <div className='flex flex-col gap-1' data-testid='jev-question-row'>
      <div className='flex items-start gap-1.5'>
        <Input
          value={name}
          disabled={disabled}
          placeholder='name (e.g. urgent)'
          aria-label='Question name'
          data-testid='jev-question-name'
          className='h-8 text-xs w-[120px] shrink-0'
          onChange={(event) => setName(event.target.value)}
          onBlur={commit}
        />
        <Input
          value={text}
          disabled={disabled}
          placeholder='Does the item ask for action within 24 hours?'
          aria-label='Yes/no question'
          data-testid='jev-question-text'
          className='h-8 text-xs flex-1'
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
        />
        {!disabled && (
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='h-8 w-8 shrink-0'
            aria-label={`Remove question ${question.name}`}
            onClick={onRemove}
          >
            <X className='size-3.5' />
          </Button>
        )}
      </div>
      {error && <p className='text-xs text-red-500'>{error}</p>}
    </div>
  );
}

// --- the panel ---------------------------------------------------------------

/**
 * The Jev Classifier's configuration in words and chips. It owns the Item,
 * Labels, Yes/No questions, uncertain fallback, classification question and
 * list concurrency params and writes them back in the backend's unchanged
 * `name: text` format; Context keeps its generic control.
 */
export function JevClassifierPanel({
  nodeId,
  struct,
  onChange,
  disabled = false,
}: JevClassifierPanelProps) {
  const { node, nodes, edges } = useNodeStore(
    useShallow((state) => ({
      node: state.nodes.find((candidate) => candidate.id === nodeId) as
        | CustomNode
        | undefined,
      nodes: state.nodes,
      edges: state.edges,
    })),
  );

  const config = useMemo(() => getJevConfig(struct), [struct]);
  const errors = useMemo(() => validateJevConfig(config), [config]);
  const catalog = useMemo(() => getJevCatalogInfo(struct), [struct]);
  const sample = useMemo(
    () =>
      node ? resolveBoundParamSample(node, nodes, edges, 'item') : undefined,
    [node, nodes, edges],
  );
  const itemParam = struct.params?.find((param) => param.field_id === 'item');
  const itemPortId = itemParam?.bindable_config?.input_port?.id;
  const itemConnected = Boolean(
    itemPortId &&
      edges.some(
        (edge) => edge.target === nodeId && edge.targetHandle === itemPortId,
      ),
  );
  const summary = useMemo(
    () =>
      readJevResult((node?.data as { result?: unknown } | undefined)?.result),
    [node?.data],
  );

  const errorAt = (path: string): string | null =>
    errors.find((error) => error.path === path)?.message ?? null;
  const labelsError = errorAt('labels');

  // Item: text or JSON.
  const itemValue = config.item;
  const itemIsStructured =
    itemValue !== null &&
    typeof itemValue === 'object' &&
    itemValue !== undefined;
  const [itemMode, setItemMode] = useState<'text' | 'json'>(
    itemIsStructured ? 'json' : 'text',
  );
  const [itemDraft, setItemDraft] = useDraft(
    typeof itemValue === 'string' ? itemValue : '',
  );

  const commitLabels = (labels: JevLabel[]) =>
    onChange('labels', formatLabels(labels));
  const commitQuestions = (questions: JevQuestion[]) =>
    onChange('yes_no_questions', formatQuestions(questions));

  const [showMore, setShowMore] = useState(false);
  const [instructionsDraft, setInstructionsDraft] = useDraft(
    config.labelInstructions,
  );
  const [uncertainDraft, setUncertainDraft] = useDraft(config.uncertainLabel);
  const [thresholdDraft, setThresholdDraft] = useDraft(
    Math.round(config.uncertainThreshold * 100),
  );

  const offeredCount = config.labels.filter(
    (label) => label.name !== config.uncertainLabel,
  ).length;
  const [addingLabel, setAddingLabel] = useState(false);
  // Labels as chips, or as the backend's own `label: criteria` lines for
  // pasting a list; both views write the same text.
  const [labelsMode, setLabelsMode] = useState<'chips' | 'text'>('chips');
  const labelsText = formatLabels(config.labels);
  const [labelsDraft, setLabelsDraft] = useDraft(labelsText);
  const [concurrencyDraft, setConcurrencyDraft] = useDraft(
    config.maxConcurrency,
  );

  return (
    <div
      className='w-full mb-4 px-3 flex flex-col gap-4'
      data-testid='jev-panel'
    >
      {/* Model and cost in one line; the node description above the tabs
          already says what the node does. */}
      <div
        className='flex flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-lg border border-border bg-muted-background px-3 py-1.5 text-xs'
        data-testid='jev-header'
      >
        <Tags className='size-3.5 shrink-0 text-muted-foreground' />
        <span className='font-medium'>
          {catalog.provider}
          {catalog.model ? ` (${catalog.model})` : ''} ·
        </span>
        <span className='text-muted-foreground'>no prompt to write ·</span>
        <span className='text-muted-foreground' data-testid='jev-cost'>
          {catalog.costPerItem} request per item, however many labels and
          questions
          {catalog.costNote?.includes('no list price')
            ? ', billed at the contract rate'
            : ''}
        </span>
      </div>

      {/* After a run: the answer first, so a click on a finished node reads
          back what Jev decided before the knobs that produced it. */}
      {summary && (
        <div data-testid='jev-run-result'>
          <div className='flex items-center gap-1 mb-1'>
            <Label className={sectionLabel}>Last run</Label>
            <span className={hint}>
              {summary.kind === 'single'
                ? `${catalog.costPerItem} request`
                : `${summary.count * catalog.costPerItem} requests`}
              {summary.kind === 'single' && summary.classification.model
                ? ` · answered by ${summary.classification.model}`
                : ''}
            </span>
          </div>
          <JevResultCard summary={summary} />
          {summary.kind === 'single' && (
            <p className={cn(hint, 'mt-1')}>
              Threshold {formatPercent(config.uncertainThreshold)}:{' '}
              {summary.classification.uncertain
                ? `the top label (${summary.classification.topLabel} at ${formatPercent(summary.classification.topProbability)}) was below it, so the answer is "${summary.classification.label}".`
                : 'the top label cleared it.'}
            </p>
          )}
        </div>
      )}

      {/* Item */}
      <div>
        <div className='flex items-center justify-between gap-2 mb-1 min-h-[28px]'>
          <div className='flex items-center gap-1'>
            <Label className={cn(sectionLabel, 'label-required')}>Item</Label>
            <InfoTip text='The text or JSON to classify. Connect an upstream output, or type a sample. A list (or a JSON array) classifies every entry and adds a spreadsheet of the results.' />
            {itemConnected && (
              <span className='flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'>
                <Cable className='size-3 shrink-0' />
                <span>from {sample?.sourceName ?? 'the connected node'}</span>
              </span>
            )}
          </div>
          {!itemConnected && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 px-2 text-xs'
              data-testid='jev-item-mode-toggle'
              disabled={disabled}
              onClick={() => setItemMode(itemMode === 'text' ? 'json' : 'text')}
            >
              {itemMode === 'text' ? (
                <>
                  <Braces className='size-3.5' /> Edit as JSON
                </>
              ) : (
                <>
                  <Type className='size-3.5' /> Edit as text
                </>
              )}
            </Button>
          )}
        </div>
        {itemConnected ? (
          <div
            className='rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground'
            data-testid='jev-item-bound'
          >
            {sample ? (
              <>
                <span className='font-medium text-foreground'>
                  Last value from {sample.sourceName}:
                </span>{' '}
                <span className='font-mono break-all'>
                  {previewValue(sample.value)}
                </span>
                {Array.isArray(sample.value) && (
                  <span>
                    {' '}
                    · a list of {sample.value.length}: every entry is classified
                  </span>
                )}
              </>
            ) : (
              'Classified at run time from the connected output (no run yet, so there is no sample to show).'
            )}
          </div>
        ) : itemMode === 'json' ? (
          <JsonEditorField
            value={itemIsStructured ? itemValue : itemDraft}
            rows={6}
            disabled={disabled}
            placeholder='{"subject": "Invoice total is wrong", "body": "..."} or ["first item", "second item"]'
            aria-label='Item as JSON'
            data-testid='jev-item-json'
            onCommit={(value) => onChange('item', value as InputValue)}
          />
        ) : (
          <>
            <Textarea
              value={itemDraft}
              disabled={disabled}
              rows={3}
              placeholder={
                itemParam?.placeholder ||
                'e.g. Customer email: the invoice total is wrong and I was charged twice'
              }
              aria-label='Item'
              data-testid='jev-item-text'
              className='text-xs'
              onChange={(event) => setItemDraft(event.target.value)}
              onBlur={() => {
                if (
                  itemDraft !== (typeof itemValue === 'string' ? itemValue : '')
                ) {
                  onChange('item', itemDraft);
                }
              }}
            />
            {sample && !disabled && (
              <div className='mt-1 flex flex-wrap items-center gap-1.5'>
                <span className={hint}>Suggestion:</span>
                <button
                  type='button'
                  className='rounded-full border border-border px-2 py-0.5 text-xs hover:border-blue-400 truncate max-w-full'
                  data-testid='jev-item-suggestion'
                  title={previewValue(sample.value, 400)}
                  onClick={() => {
                    const text =
                      typeof sample.value === 'string'
                        ? sample.value
                        : JSON.stringify(sample.value, null, 2);
                    setItemDraft(text);
                    onChange('item', text);
                  }}
                >
                  last value from {sample.sourceName}:{' '}
                  {previewValue(sample.value, 60)}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Labels */}
      <div>
        <div className='flex items-center gap-1 mb-1 min-h-[28px]'>
          <Label className={cn(sectionLabel, 'label-required')}>Labels</Label>
          <InfoTip text='Your own labels, 2 to 255. Each has a lower_snake_case name (it becomes a Switch case name) and the criteria Jev reads to pick it. Click a chip to edit it.' />
          <span className={cn(hint, 'ml-auto')} data-testid='jev-labels-count'>
            {offeredCount} of {JEV_MAX_LABELS}
          </span>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-7 px-2 text-xs'
            data-testid='jev-labels-mode-toggle'
            disabled={disabled}
            onClick={() =>
              setLabelsMode(labelsMode === 'chips' ? 'text' : 'chips')
            }
          >
            {labelsMode === 'chips' ? (
              <>
                <Type className='size-3.5' /> Edit as text
              </>
            ) : (
              <>
                <Tags className='size-3.5' /> Edit as chips
              </>
            )}
          </Button>
        </div>
        {labelsMode === 'text' ? (
          <Textarea
            value={labelsDraft}
            disabled={disabled}
            rows={Math.min(12, Math.max(4, config.labels.length + 1))}
            placeholder={
              'billing: The item is about invoices, charges or refunds\ntechnical: The item reports a bug or an outage\nother: Anything else'
            }
            aria-label='Labels, one "label: criteria" line each'
            data-testid='jev-labels-text'
            className='text-xs font-mono'
            onChange={(event) => setLabelsDraft(event.target.value)}
            onBlur={() => {
              if (labelsDraft !== labelsText) {
                onChange('labels', labelsDraft);
              }
            }}
          />
        ) : null}
        {labelsMode === 'chips' ? (
          <div className='flex flex-wrap gap-1.5' data-testid='jev-labels'>
            {config.labels.map((label, index) => (
              <LabelChip
                key={`${index}-${label.name}`}
                label={label}
                index={index}
                error={errorAt(`labels[${index}]`)}
                isUncertain={label.name === config.uncertainLabel}
                disabled={disabled}
                onChange={(next) =>
                  commitLabels(
                    config.labels.map((entry, position) =>
                      position === index ? next : entry,
                    ),
                  )
                }
                onRemove={() =>
                  commitLabels(
                    config.labels.filter((_, position) => position !== index),
                  )
                }
              />
            ))}
            {addingLabel ? (
              <LabelChip
                label={EMPTY_LABEL}
                index={config.labels.length}
                error={null}
                isUncertain={false}
                disabled={disabled}
                onChange={(next) => {
                  setAddingLabel(false);
                  commitLabels([...config.labels, next]);
                }}
                onRemove={() => setAddingLabel(false)}
              />
            ) : (
              !disabled && (
                <button
                  type='button'
                  className='inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-blue-400 hover:text-foreground'
                  data-testid='jev-add-label'
                  onClick={() => setAddingLabel(true)}
                >
                  <Plus className='size-3' /> Add label
                </button>
              )
            )}
          </div>
        ) : null}
        {labelsError && (
          <p
            className='text-xs text-red-500 mt-1'
            data-testid='jev-labels-error'
          >
            {labelsError}
          </p>
        )}
        {!labelsError && config.labels.length === 0 && (
          <p className={cn(hint, 'mt-1')}>
            Example: <span className='font-mono'>billing</span> · The item is
            about invoices, charges or refunds.
          </p>
        )}
      </div>

      {/* Yes/No questions */}
      <div>
        <div className='flex items-center gap-1 mb-1 min-h-[28px]'>
          <Label className={sectionLabel}>Yes/No questions</Label>
          <InfoTip text='Optional. Each question comes back as the probability that the answer is yes, alongside the label, in the same request.' />
        </div>
        <div className='flex flex-col gap-1.5' data-testid='jev-questions'>
          {config.questions.map((question, index) => (
            <QuestionRow
              key={`${index}-${question.name}`}
              question={question}
              error={errorAt(`yes_no_questions[${index}]`)}
              disabled={disabled}
              onChange={(next) =>
                commitQuestions(
                  config.questions.map((entry, position) =>
                    position === index ? next : entry,
                  ),
                )
              }
              onRemove={() =>
                commitQuestions(
                  config.questions.filter((_, position) => position !== index),
                )
              }
            />
          ))}
          {!disabled && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='h-7 w-fit px-2 text-xs'
              data-testid='jev-add-question'
              onClick={() =>
                commitQuestions([
                  ...config.questions,
                  {
                    name: `question_${config.questions.length + 1}`,
                    question: `question_${config.questions.length + 1}`,
                  },
                ])
              }
            >
              <Plus className='size-3' /> Add a yes/no question
            </Button>
          )}
        </div>
      </div>

      {/* Uncertain fallback, in words */}
      <div data-testid='jev-uncertain'>
        <div className='flex items-center gap-1 mb-1 min-h-[28px]'>
          <Label className={sectionLabel}>When Jev is unsure</Label>
          <InfoTip text="If the top label's probability is below this threshold, the node answers with the uncertain label instead, so a Switch can send doubtful items to a person. Set 0% to always take the top label." />
        </div>
        <div className='flex flex-wrap items-center gap-x-1.5 gap-y-2 text-xs'>
          <span>If the top label&apos;s probability is below</span>
          <Input
            type='number'
            min={0}
            max={100}
            step={1}
            value={thresholdDraft}
            disabled={disabled}
            aria-label='Uncertain threshold (percent)'
            data-testid='jev-threshold'
            className='h-7 w-[64px] text-xs text-right'
            onChange={(event) => setThresholdDraft(Number(event.target.value))}
            onBlur={() => {
              const clamped = Math.max(0, Math.min(100, thresholdDraft || 0));
              setThresholdDraft(clamped);
              if (clamped / 100 !== config.uncertainThreshold) {
                onChange('uncertain_threshold', clamped / 100);
              }
            }}
          />
          <span>%, answer</span>
          <Input
            value={uncertainDraft}
            disabled={disabled}
            aria-label='Uncertain label'
            data-testid='jev-uncertain-label'
            className={cn(
              'h-7 w-[140px] text-xs font-mono',
              errorAt('uncertain_label') && 'border-red-500',
            )}
            onChange={(event) =>
              setUncertainDraft(
                event.target.value.toLowerCase().replace(/\s+/g, '_'),
              )
            }
            onBlur={() => {
              const next = uncertainDraft.trim();
              if (next && next !== config.uncertainLabel) {
                onChange('uncertain_label', next);
              }
            }}
          />
          <span>instead.</span>
        </div>
        <Slider
          min={0}
          max={100}
          step={5}
          value={[thresholdDraft]}
          disabled={disabled}
          aria-label='Uncertain threshold'
          className='mt-2'
          onValueChange={(values) => setThresholdDraft(values[0])}
          onValueCommit={(values) => {
            if (values[0] / 100 !== config.uncertainThreshold) {
              onChange('uncertain_threshold', values[0] / 100);
            }
          }}
        />
        {errorAt('uncertain_label') && (
          <p className='text-xs text-red-500 mt-1'>
            {errorAt('uncertain_label')}
          </p>
        )}
        {errorAt('uncertain_threshold') && (
          <p className='text-xs text-red-500 mt-1'>
            {errorAt('uncertain_threshold')}
          </p>
        )}
        <p className={cn(hint, 'mt-1')}>
          {thresholdDraft === 0
            ? 'The top label is always the answer.'
            : `A Switch downstream can route "${config.uncertainLabel}" to a person.`}
        </p>
      </div>

      {/* More: the classification question */}
      <div>
        <button
          type='button'
          className='flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
          data-testid='jev-more-toggle'
          onClick={() => setShowMore(!showMore)}
        >
          <ChevronDown
            className={cn(
              'size-3.5 transition-transform',
              showMore && 'rotate-180',
            )}
          />
          {showMore ? 'Fewer settings' : 'More settings'}
        </button>
        {showMore && (
          <div className='mt-2'>
            <div className='flex items-center gap-1 mb-1'>
              <Label className='text-xs'>Classification question</Label>
              <InfoTip text='The question Jev answers when it picks a label. The default works for most cases.' />
            </div>
            <Input
              value={instructionsDraft}
              disabled={disabled}
              placeholder={JEV_DEFAULT_LABEL_INSTRUCTIONS}
              aria-label='Classification question'
              data-testid='jev-label-instructions'
              className='h-8 text-xs'
              onChange={(event) => setInstructionsDraft(event.target.value)}
              onBlur={() => {
                if (instructionsDraft !== config.labelInstructions) {
                  onChange('label_instructions', instructionsDraft);
                }
              }}
            />
            {/* Advanced params have no generic control on this line, so the
                list concurrency lives here. */}
            <div className='flex items-center gap-1 mb-1 mt-3'>
              <Label className='text-xs'>Items classified at once</Label>
              <InfoTip
                text={`For a list: how many items are sent to Jev in parallel, 1 to ${JEV_MAX_CONCURRENCY}. Each item is still one request.`}
              />
            </div>
            <div className='flex items-center gap-1.5 text-xs'>
              <Input
                type='number'
                min={1}
                max={JEV_MAX_CONCURRENCY}
                step={1}
                value={concurrencyDraft}
                disabled={disabled}
                aria-label='Items classified at once'
                data-testid='jev-max-concurrency'
                className='h-8 w-[72px] text-xs text-right'
                onChange={(event) =>
                  setConcurrencyDraft(Number(event.target.value))
                }
                onBlur={() => {
                  const clamped = Math.max(
                    1,
                    Math.min(
                      JEV_MAX_CONCURRENCY,
                      Math.round(concurrencyDraft) ||
                        JEV_DEFAULT_MAX_CONCURRENCY,
                    ),
                  );
                  setConcurrencyDraft(clamped);
                  if (clamped !== config.maxConcurrency) {
                    onChange('max_concurrency', clamped);
                  }
                }}
              />
              <span className={hint}>
                of up to {JEV_MAX_CONCURRENCY} (default{' '}
                {JEV_DEFAULT_MAX_CONCURRENCY}); only matters for a list.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Preview / plan */}
      <div
        className={cn(
          'rounded-md px-2 py-1.5 text-xs',
          errors.length
            ? 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
            : 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300',
        )}
        data-testid='jev-preview'
      >
        {errors.length ? (
          <>
            <span className='font-medium'>Not ready to run: </span>
            {(errors[0] as JevConfigError).message}
          </>
        ) : (
          <>
            {describeJevPlan(config)}{' '}
            <span className='opacity-80'>
              Runs at execution ({catalog.costPerItem} request per item); there
              is no dry run from the editor.
            </span>
          </>
        )}
      </div>
    </div>
  );
}
